import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  Autocomplete,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  CreateTaskRequest,
  TaskType,
  TaskPriority,
  TaskRecord
} from '@/types/taskTypes';
import { TaskService } from '@/services/taskService';

interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  initialData?: Partial<CreateTaskRequest>;
}

// Mock data for assignees - replace with real data from user service
const mockAssignees = [
  { id: 'user1', name: 'Sarah Johnson', email: 'sarah@company.com' },
  { id: 'user2', name: 'Mike Chen', email: 'mike@company.com' },
  { id: 'user3', name: 'Alex Rodriguez', email: 'alex@company.com' },
  { id: 'user4', name: 'Lisa Park', email: 'lisa@company.com' }
];

// Common tags for suggestions
const commonTags = [
  'urgent',
  'follow-up',
  'review',
  'approval',
  'documentation',
  'compliance',
  'legal',
  'medical',
  'intake',
  'template'
];

export const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  open,
  onClose,
  initialData
}) => {
  const [formData, setFormData] = useState<CreateTaskRequest>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    type: initialData?.type || TaskType.Custom,
    priority: initialData?.priority || TaskPriority.Medium,
    assignedToId: initialData?.assignedToId || '',
    dueDate: initialData?.dueDate || '',
    relatedRecord: initialData?.relatedRecord || undefined,
    sessionId: initialData?.sessionId || '',
    templateId: initialData?.templateId || '',
    personId: initialData?.personId || '',
    tags: initialData?.tags || []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tagInput, setTagInput] = useState('');

  const handleInputChange = (field: keyof CreateTaskRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag && !formData.tags?.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((tag) => tag !== tagToRemove) || []
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.type) {
      newErrors.type = 'Task type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      await TaskService.createTask(formData);
      onClose();
      // Reset form
      setFormData({
        title: '',
        description: '',
        type: TaskType.Custom,
        priority: TaskPriority.Medium,
        assignedToId: '',
        dueDate: '',
        tags: []
      });
    } catch (error) {
      console.error('Failed to create task:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '12px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
            pt: 3,
            px: 3
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 600, color: 'text.primary' }}>
            Create New Task
          </Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small" sx={{ borderRadius: '6px' }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, px: 3, pb: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Title */}
            <TextField
              placeholder="Task title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!errors.title}
              helperText={errors.title}
              fullWidth
              required
              autoFocus
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: 'divider'
                  }
                }
              }}
            />

            {/* Description - Optional, single line */}
            <TextField
              placeholder="Description (optional)"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: 'divider'
                  }
                }
              }}
            />

            {/* Type and Priority Row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth required error={!!errors.type} size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Task Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Task Type"
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  sx={{
                    borderRadius: '6px',
                    fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    }
                  }}
                >
                  <MenuItem value={TaskType.Approval} sx={{ fontSize: '13px' }}>Approval</MenuItem>
                  <MenuItem value={TaskType.Review} sx={{ fontSize: '13px' }}>Review</MenuItem>
                  <MenuItem value={TaskType.FollowUp} sx={{ fontSize: '13px' }}>Follow Up</MenuItem>
                  <MenuItem value={TaskType.Information} sx={{ fontSize: '13px' }}>Information</MenuItem>
                  <MenuItem value={TaskType.Custom} sx={{ fontSize: '13px' }}>Custom</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) =>
                    handleInputChange('priority', e.target.value)
                  }
                  sx={{
                    borderRadius: '6px',
                    fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    }
                  }}
                >
                  <MenuItem value={TaskPriority.Low} sx={{ fontSize: '13px' }}>Low</MenuItem>
                  <MenuItem value={TaskPriority.Medium} sx={{ fontSize: '13px' }}>Medium</MenuItem>
                  <MenuItem value={TaskPriority.High} sx={{ fontSize: '13px' }}>High</MenuItem>
                  <MenuItem value={TaskPriority.Critical} sx={{ fontSize: '13px' }}>Critical</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Assignee and Due Date Row */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '13px' }}>Assign To</InputLabel>
                <Select
                  value={formData.assignedToId}
                  label="Assign To"
                  onChange={(e) =>
                    handleInputChange('assignedToId', e.target.value)
                  }
                  sx={{
                    borderRadius: '6px',
                    fontSize: '13px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '13px' }}>
                    <em>Unassigned</em>
                  </MenuItem>
                  {mockAssignees.map((assignee) => (
                    <MenuItem key={assignee.id} value={assignee.id} sx={{ fontSize: '13px' }}>
                      {assignee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="Due Date"
                value={formData.dueDate ? new Date(formData.dueDate) : null}
                onChange={(date: Date | null) =>
                  handleInputChange('dueDate', date?.toISOString() || '')
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '13px',
                        '& fieldset': {
                          borderColor: 'divider'
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '13px'
                      }
                    }
                  }
                }}
              />
            </Box>

            {/* Related Record IDs (for advanced use) */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Session ID"
                value={formData.sessionId}
                onChange={(e) => handleInputChange('sessionId', e.target.value)}
                fullWidth
                size="small"
                placeholder="Optional: Link to a session"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '13px',
                    '& fieldset': {
                      borderColor: 'divider'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '11px'
                  }
                }}
              />

              <TextField
                label="Template ID"
                value={formData.templateId}
                onChange={(e) =>
                  handleInputChange('templateId', e.target.value)
                }
                fullWidth
                size="small"
                placeholder="Optional: Link to a template"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '13px',
                    '& fieldset': {
                      borderColor: 'divider'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px'
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '11px'
                  }
                }}
              />
            </Box>

            <TextField
              label="Person ID"
              value={formData.personId}
              onChange={(e) => handleInputChange('personId', e.target.value)}
              fullWidth
              size="small"
              placeholder="Optional: Link to a person"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: 'divider'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '13px'
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '11px'
                }
              }}
            />

            {/* Tags */}
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: 'text.secondary', mb: 1 }}>
                Tags
              </Typography>

              <Autocomplete
                freeSolo
                options={commonTags}
                inputValue={tagInput}
                onInputChange={(_, value) => setTagInput(value)}
                onChange={(_, value) => {
                  if (typeof value === 'string') {
                    handleAddTag(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tagInput.trim()) {
                    e.preventDefault();
                    handleAddTag(tagInput.trim());
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Add tags..."
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        fontSize: '13px',
                        '& fieldset': {
                          borderColor: 'divider'
                        }
                      }
                    }}
                  />
                )}
              />

              {/* Current Tags */}
              {formData.tags && formData.tags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                      sx={{
                        fontSize: '11px',
                        height: 24,
                        borderRadius: '4px',
                        backgroundColor: theme => theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.05)' 
                          : '#f3f4f6',
                        color: 'text.secondary',
                        border: 'none',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              color: 'text.secondary',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ 
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              backgroundColor: '#1a1a1a',
              color: 'white',
              '&:hover': {
                backgroundColor: '#000'
              }
            }}
          >
            {loading ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};
